import { useState } from 'react';

type RenderMode = 'create-render' | 'lift-adjust' | 'upscale-enhance' | 'rerender-style' | 'render-3d-model' | 'video-animate';

interface RenderJob {
  id: string;
  mode: RenderMode;
  label: string;
  status: 'processing' | 'done';
  progress: number;
  resultUrl: string;
}

const RENDER_MODES: { id: RenderMode; label: string; icon: string; desc: string; source: string }[] = [
  { id: 'create-render', label: 'Create & Render', icon: 'ri-magic-line', desc: 'Turn sketches or drawings into photorealistic renders instantly', source: 'Rendair AI' },
  { id: 'lift-adjust', label: 'Lift & Adjust', icon: 'ri-contrast-2-line', desc: 'Change materials, lighting, colors and textures on existing renders', source: 'Rendair AI' },
  { id: 'upscale-enhance', label: 'Upscale & Enhance', icon: 'ri-hd-line', desc: 'Make renders sharper, bigger and client-ready in one click', source: 'Rendair AI' },
  { id: 'rerender-style', label: 'Re-Render Style', icon: 'ri-palette-line', desc: 'Apply a completely new architectural style to any existing image', source: 'Rerender AI' },
  { id: 'render-3d-model', label: 'Render 3D Model', icon: 'ri-box-3-line', desc: 'Render any 3D model from any angle with photorealistic quality', source: 'Rendair AI' },
  { id: 'video-animate', label: 'Video & Animate', icon: 'ri-film-line', desc: 'Transform still renders into smooth animated presentations', source: 'Rendair AI' },
];

const STYLE_PRESETS = [
  { id: 'photorealistic', label: 'Photorealistic', img: 'https://public.readdy.ai/ai/img_res/1ed11a75c0805661ba336a4b1b48ed3c.jpg' },
  { id: 'sketch', label: 'Architectural Sketch', img: 'https://readdy.ai/api/search-image?query=architectural%20hand%20drawn%20sketch%20pencil%20line%20drawing%20house%20exterior%20artistic%20rendering%20design%20concept&width=200&height=130&seq=rr-sketch&orientation=landscape' },
  { id: 'watercolor', label: 'Watercolor', img: 'https://readdy.ai/api/search-image?query=watercolor%20architectural%20illustration%20house%20exterior%20soft%20colors%20artistic%20rendering%20beautiful%20painterly%20style&width=200&height=130&seq=rr-watercolor&orientation=landscape' },
  { id: 'night', label: 'Night Render', img: 'https://readdy.ai/api/search-image?query=night%20architectural%20rendering%20modern%20house%20exterior%20dramatic%20lighting%20glowing%20windows%20dark%20sky%20reflections%20luxury&width=200&height=130&seq=rr-night&orientation=landscape' },
  { id: 'aerial', label: 'Aerial View', img: 'https://readdy.ai/api/search-image?query=aerial%20top%20down%20architectural%20rendering%20modern%20house%20roof%20view%20landscaping%20pool%20driveway%20professional%20visualization&width=200&height=130&seq=rr-aerial&orientation=landscape' },
  { id: 'wireframe', label: 'Wireframe', img: 'https://readdy.ai/api/search-image?query=architectural%20wireframe%203D%20model%20house%20structure%20technical%20visualization%20clean%20lines%20blueprint%20style&width=200&height=130&seq=rr-wire&orientation=landscape' },
];

const MATERIAL_PRESETS = [
  { id: 'concrete', label: 'Concrete', color: '#9CA3AF' },
  { id: 'wood', label: 'Warm Wood', color: '#92400E' },
  { id: 'brick', label: 'Red Brick', color: '#B45309' },
  { id: 'glass', label: 'Glass', color: '#BAE6FD' },
  { id: 'marble', label: 'Marble', color: '#F3F4F6' },
  { id: 'steel', label: 'Steel', color: '#6B7280' },
];

const LIGHTING_PRESETS = [
  { id: 'golden-hour', label: 'Golden Hour', icon: 'ri-sun-line' },
  { id: 'overcast', label: 'Overcast', icon: 'ri-cloud-line' },
  { id: 'twilight', label: 'Twilight', icon: 'ri-moon-line' },
  { id: 'studio', label: 'Studio', icon: 'ri-lightbulb-line' },
];

const SAMPLE_RESULTS = [
  'https://readdy.ai/api/search-image?query=stunning%20photorealistic%20architectural%20rendering%20modern%20luxury%20house%20exterior%20golden%20hour%20lighting%20beautiful%20landscaping%20professional%20quality%20high%20resolution&width=800&height=500&seq=render-result1&orientation=landscape',
  'https://readdy.ai/api/search-image?query=photorealistic%20contemporary%20house%20exterior%20render%20dramatic%20twilight%20lighting%20glowing%20windows%20reflective%20pool%20professional%20architectural%20visualization&width=800&height=500&seq=render-result2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=ultra%20realistic%20minimalist%20house%20exterior%20render%20bright%20daylight%20clean%20white%20surfaces%20glass%20walls%20professional%20architectural%20photography%20quality&width=800&height=500&seq=render-result3&orientation=landscape',
];

interface AIRenderStudioProps {
  darkMode: boolean;
}

export default function AIRenderStudio({ darkMode }: AIRenderStudioProps) {
  const [activeMode, setActiveMode] = useState<RenderMode>('create-render');
  const [selectedStyle, setSelectedStyle] = useState('photorealistic');
  const [selectedMaterial, setSelectedMaterial] = useState('concrete');
  const [selectedLighting, setSelectedLighting] = useState('golden-hour');
  const [uploadedSource, setUploadedSource] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [activeJobResult, setActiveJobResult] = useState<string | null>(null);
  const [shareToast, setShareToast] = useState('');

  const showToast = (msg: string) => {
    setShareToast(msg);
    setTimeout(() => setShareToast(''), 3000);
  };

  const handleDownloadResult = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'render-result.png';
    a.target = '_blank';
    a.click();
  };

  const handleShareResult = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Render URL copied to clipboard!');
    } catch {
      showToast('Link copied!');
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setUploadedSource(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRender = () => {
    setIsRendering(true);
    setRenderProgress(0);
    const resultUrl = SAMPLE_RESULTS[jobs.length % SAMPLE_RESULTS.length];
    const newJob: RenderJob = {
      id: Date.now().toString(),
      mode: activeMode,
      label: RENDER_MODES.find(m => m.id === activeMode)?.label || '',
      status: 'processing',
      progress: 0,
      resultUrl,
    };
    setJobs(prev => [newJob, ...prev]);

    let prog = 0;
    const interval = setInterval(() => {
      prog += Math.random() * 15 + 10;
      if (prog >= 100) {
        prog = 100;
        clearInterval(interval);
        setIsRendering(false);
        setRenderProgress(100);
        setActiveJobResult(resultUrl);
        setJobs(prev => prev.map(j => j.id === newJob.id ? { ...j, status: 'done', progress: 100 } : j));
      } else {
        setRenderProgress(Math.round(prog));
        setJobs(prev => prev.map(j => j.id === newJob.id ? { ...j, progress: Math.round(prog) } : j));
      }
    }, 350);
  };

  const needsUpload = ['lift-adjust', 'upscale-enhance', 'rerender-style', 'render-3d-model', 'video-animate'].includes(activeMode);

  return (
    <div className="space-y-5">
      <div className="mb-2">
        <h3 className="text-base font-bold text-gray-900 mb-1">AI Render Studio</h3>
        <p className="text-xs text-gray-400">Powered by Rendair AI &amp; Rerender AI — professional renders in seconds</p>
      </div>

      {/* Mode Tabs */}
      <div className="space-y-2">
        {RENDER_MODES.map(mode => (
          <button
            key={mode.id}
            onClick={() => setActiveMode(mode.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer text-left ${
              activeMode === mode.id
                ? 'border-orange-300 bg-orange-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${activeMode === mode.id ? 'bg-orange-100' : 'bg-gray-100'}`}>
              <i className={`${mode.icon} text-lg ${activeMode === mode.id ? 'text-orange-500' : 'text-gray-400'}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold whitespace-nowrap ${activeMode === mode.id ? 'text-orange-600' : 'text-gray-800'}`}>{mode.label}</span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{mode.source}</span>
              </div>
              <p className="text-xs text-gray-500 leading-tight mt-0.5">{mode.desc}</p>
            </div>
            {activeMode === mode.id && (
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-orange-500 text-lg"></i>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Config Panel */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">

        {/* Upload Source */}
        {needsUpload && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">Source Image / 3D Model</p>
            {uploadedSource ? (
              <div className="relative rounded-lg overflow-hidden" style={{ height: '130px' }}>
                <img src={uploadedSource} alt="Source" className="w-full h-full object-cover" />
                <button
                  onClick={() => setUploadedSource(null)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
                <div className="absolute bottom-2 left-2 bg-black/60 rounded px-2 py-0.5">
                  <span className="text-xs text-green-400">Source loaded</span>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-5 cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors">
                <i className="ri-upload-cloud-line text-2xl text-gray-400 mb-1"></i>
                <span className="text-xs text-gray-500">Upload image, sketch or 3D file</span>
                <span className="text-xs text-gray-400 mt-0.5">JPG, PNG, OBJ, FBX</span>
                <input type="file" accept="image/*,.obj,.fbx" className="hidden" onChange={handleUpload} />
              </label>
            )}
          </div>
        )}

        {/* Prompt */}
        {(activeMode === 'create-render' || activeMode === 'rerender-style') && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">Design Description</p>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. Modern farmhouse with warm wood cladding, large windows, surrounded by mature trees..."
              className="w-full h-20 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 resize-none"
              maxLength={300}
            />
            <p className="text-xs text-gray-400 mt-1">{prompt.length}/300</p>
          </div>
        )}

        {/* Style Presets */}
        {(activeMode === 'create-render' || activeMode === 'rerender-style') && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">Render Style</p>
            <div className="grid grid-cols-3 gap-2">
              {STYLE_PRESETS.map(style => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedStyle === style.id ? 'ring-2 ring-orange-400' : 'hover:ring-1 hover:ring-gray-300'
                  }`}
                  style={{ height: '70px' }}
                >
                  <img src={style.img} alt={style.label} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <p className="absolute bottom-1 left-0 right-0 text-center text-white text-xs font-medium leading-tight px-1">{style.label}</p>
                  {selectedStyle === style.id && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Material & Lighting */}
        {(activeMode === 'lift-adjust' || activeMode === 'create-render') && (
          <>
            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">Material Override</p>
              <div className="flex flex-wrap gap-2">
                {MATERIAL_PRESETS.map(mat => (
                  <button
                    key={mat.id}
                    onClick={() => setSelectedMaterial(mat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      selectedMaterial === mat.id ? 'ring-2 ring-orange-400 bg-orange-50 border-orange-200' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: mat.color }}></div>
                    <span className="text-gray-700">{mat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">Lighting</p>
              <div className="grid grid-cols-2 gap-2">
                {LIGHTING_PRESETS.map(light => (
                  <button
                    key={light.id}
                    onClick={() => setSelectedLighting(light.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedLighting === light.id ? 'bg-orange-50 border border-orange-300 text-orange-600' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${light.icon} text-sm`}></i>
                    {light.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Render Button */}
        <button
          onClick={handleRender}
          disabled={isRendering}
          className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
            isRendering ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-500/30'
          }`}
        >
          {isRendering ? (
            <><i className="ri-loader-4-line animate-spin"></i> Rendering... {renderProgress}%</>
          ) : (
            <><i className="ri-magic-line"></i> Start Render</>
          )}
        </button>

        {isRendering && (
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-300"
              style={{ width: `${renderProgress}%` }}
            ></div>
          </div>
        )}
      </div>

      {/* Result Preview */}
      {activeJobResult && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-green-500"></i>
              </div>
              <span className="text-sm font-semibold text-gray-900">Render Complete</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleDownloadResult(activeJobResult)}
                className="px-3 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg text-xs font-medium hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-download-line text-xs"></i> Download
              </button>
              <button
                onClick={() => handleShareResult(activeJobResult)}
                className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-share-line text-xs"></i> Share
              </button>
            </div>
          </div>
          <div style={{ height: '280px' }}>
            <img src={activeJobResult} alt="Render result" className="w-full h-full object-cover object-top" />
          </div>
          <div className="px-4 py-3 flex gap-3">
            <button
              onClick={() => setActiveJobResult(null)}
              className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1"></i> Re-Render
            </button>
            <button
              onClick={() => { setActiveMode('lift-adjust'); setActiveJobResult(null); }}
              className="flex-1 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-xs font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1"></i> Lift &amp; Adjust
            </button>
          </div>
        </div>
      )}

      {/* History */}
      {jobs.filter(j => j.status === 'done').length > 1 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Render History</h4>
          <div className="grid grid-cols-3 gap-2">
            {jobs.filter(j => j.status === 'done').slice(1).map(job => (
              <button
                key={job.id}
                onClick={() => setActiveJobResult(job.resultUrl)}
                className="relative rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-orange-400 transition-all"
                style={{ height: '80px' }}
              >
                <img src={job.resultUrl} alt={job.label} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <p className="absolute bottom-1 left-0 right-0 text-center text-white text-xs font-medium">{job.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Share Toast */}
      {shareToast && (
        <div className="fixed bottom-6 right-6 px-4 py-3 bg-gray-900 text-white rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-xs"></i>
          </div>
          <span className="text-sm font-medium">{shareToast}</span>
        </div>
      )}
    </div>
  );
}
